import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search, Upload } from 'lucide-react';
import clsx from 'clsx';
import { CATEGORY_LABELS, FONT_CATALOG, type FontCategory } from '../../core/fonts/catalog';
import { ensurePreviewFonts, previewFamily } from '../../core/fonts/fontPreview';
import { listUploadedFonts, type LoadedFont } from '../../core/fonts/fontLoader';

interface Entry {
  id: string;
  family: string;
  category: FontCategory | 'upload';
  axes: string[];
  note?: string;
}

export function FontPicker({
  value,
  onChange,
  onUpload,
  uploadedVersion,
}: {
  value: string;
  onChange: (id: string) => void;
  onUpload: (file: File) => void;
  /** Bumped by the parent after an upload so the list re-reads the loader registry. */
  uploadedVersion: number;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) ensurePreviewFonts();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const entries = useMemo<Entry[]>(() => {
    const uploads: Entry[] = listUploadedFonts().map((f: LoadedFont) => ({
      id: f.id,
      family: f.family,
      category: 'upload',
      axes: f.axes.map((a) => a.tag),
      note: f.axes.length ? `${f.axes.length} Achsen` : undefined,
    }));
    const catalog: Entry[] = FONT_CATALOG.map((f) => ({
      id: f.id,
      family: f.family,
      category: f.category,
      axes: f.axes ?? [],
      note: f.note,
    }));
    return [...uploads, ...catalog];
    // uploadedVersion is the invalidation signal for the module-level registry.
  }, [uploadedVersion]);

  const current = entries.find((e) => e.id === value);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter(
      (e) =>
        e.family.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q) ||
        e.axes.some((a) => a.toLowerCase().includes(q)),
    );
  }, [entries, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, Entry[]>();
    for (const e of filtered) {
      const label = e.category === 'upload' ? 'Eigene Fonts' : CATEGORY_LABELS[e.category];
      const list = map.get(label);
      if (list) list.push(e);
      else map.set(label, [e]);
    }
    return [...map.entries()];
  }, [filtered]);

  const handleFiles = (files: FileList | null): void => {
    const file = files?.[0];
    if (file) onUpload(file);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="field-input flex items-center justify-between gap-2 text-left"
      >
        <span className="truncate" style={{ fontFamily: current ? previewFamily(current.family) : undefined }}>
          {current?.family ?? value}
        </span>
        <ChevronDown size={12} className="shrink-0 text-ink-500" />
      </button>

      {open && (
        <div
          className={clsx(
            'absolute left-0 right-0 z-50 mt-1 overflow-hidden rounded-lg border bg-ink-850 shadow-2xl shadow-black/60',
            dragOver ? 'border-accent-500' : 'border-ink-700',
          )}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
        >
          <div className="flex items-center gap-1.5 border-b border-ink-750 px-2.5 py-2">
            <Search size={12} className="shrink-0 text-ink-500" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Suchen — Name, Kategorie oder Achse (z. B. wght)"
              className="w-full bg-transparent text-[11px] text-ink-100 outline-none placeholder:text-ink-500"
            />
          </div>

          <div className="max-h-[340px] overflow-y-auto py-1">
            {grouped.length === 0 && (
              <div className="px-3 py-6 text-center text-[11px] text-ink-500">Nichts gefunden.</div>
            )}
            {grouped.map(([label, items]) => (
              <div key={label}>
                <div className="sticky top-0 bg-ink-850/95 px-3 py-1 panel-label backdrop-blur">{label}</div>
                {items.map((e) => (
                  <button
                    key={e.id}
                    type="button"
                    onClick={() => {
                      onChange(e.id);
                      setOpen(false);
                    }}
                    className={clsx(
                      'flex w-full items-baseline gap-2 px-3 py-1.5 text-left transition-colors hover:bg-ink-800',
                      e.id === value && 'bg-ink-800',
                    )}
                  >
                    <span className="w-3 shrink-0">
                      {e.id === value && <Check size={11} className="text-accent-500" />}
                    </span>
                    <span
                      className="min-w-0 flex-1 truncate text-[15px] leading-tight text-ink-100"
                      style={{ fontFamily: previewFamily(e.family) }}
                    >
                      {e.family}
                    </span>
                    {e.axes.length > 0 && (
                      <span className="shrink-0 rounded bg-ink-750 px-1 font-mono text-[9px] text-ink-400">
                        {e.axes.join(' ')}
                      </span>
                    )}
                    {e.note && <span className="shrink-0 text-[9px] text-ink-500">{e.note}</span>}
                  </button>
                ))}
              </div>
            ))}
          </div>

          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="flex w-full items-center gap-2 border-t border-ink-750 px-3 py-2 text-[11px] text-ink-300 transition-colors hover:bg-ink-800 hover:text-ink-100"
          >
            <Upload size={12} />
            Font hochladen oder hierher ziehen — .ttf, .otf, .woff
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".ttf,.otf,.woff,font/ttf,font/otf,font/woff"
            className="hidden"
            onChange={(e) => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      )}
    </div>
  );
}
